import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useRecycledLockedTickers — Feb 2026 (AORT bug).
 *
 * Fetches the admin-visible set of tickers that were moved to the
 * non-dividend universe via the Recycled Tickers modal (former symbol
 * got reassigned to a different security — e.g. AORT's CRY is now
 * the GraniteShares YieldBOOST CRCL ETF). Non-admins get an empty
 * set. Consumers use `isLocked(ticker)` to render a `>` chevron in
 * front of the symbol wherever it appears so the admin can see at a
 * glance which rows were swept up by the recycled detector.
 *
 * Refetches on window focus so a fresh lock in another tab shows up
 * without a full page reload.
 */
export function useRecycledLockedTickers() {
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';
  const [set, setSet] = useState(() => new Set());

  const load = useCallback(async () => {
    if (!isAdmin) { setSet(new Set()); return; }
    try {
      const { data } = await axios.get(`${API}/admin/recycled-locked-tickers`, {
        withCredentials: true,
      });
      const arr = Array.isArray(data?.tickers) ? data.tickers : [];
      setSet(new Set(arr.map((t) => String(t || '').toUpperCase())));
    } catch (_e) {
      setSet(new Set());
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const isLocked = useCallback(
    (ticker) => !!ticker && set.has(String(ticker).toUpperCase()),
    [set],
  );

  return { isLocked, refresh: load, set };
}

/**
 * RecycledChevron — one-liner render helper for the `>` chevron marker.
 * Renders nothing when the ticker isn't locked or the caller isn't
 * admin (the hook returns an empty set for non-admins).
 */
export function RecycledChevron({ ticker, className = '' }) {
  const { isLocked } = useRecycledLockedTickers();
  if (!isLocked(ticker)) return null;
  return (
    <span
      className={`inline-block mr-0.5 font-mono text-amber-600 dark:text-amber-400 ${className}`}
      title="Recycled-locked: this ticker's former symbol was reassigned to a different security. Move-to-dividend is blocked."
      data-testid={`recycled-chevron-${ticker}`}
    >
      &gt;
    </span>
  );
}
